/**
 * `/student/assistant` — Study Assistant.
 *
 * Sprint 1 ships only the *shell*: a draft transcript + privacy banner +
 * a disabled compose box that explains the agent is not live yet. Every
 * assistant message is labelled "Draft" so a student is never misled
 * about whether the AI is fully online.
 */

import { useState } from 'react';
import { useApiClient } from '../../api/ApiClientProvider';
import { useFetch } from '../../api/useFetch';
import { useSession } from '../../auth/SessionProvider';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/States';
import { PrivacyBanner } from '../../components/PrivacyBanner';

export function StudentAssistant() {
  const client = useApiClient();
  const { session } = useSession();
  const token = session?.token ?? '';
  const state = useFetch(
    () => client.getAssistantTranscript(token),
    [client, token],
  );
  const [draft, setDraft] = useState('');

  return (
    <section className="page" data-testid="student-assistant">
      <header className="page__header">
        <h1>Study Assistant</h1>
        <p className="muted">
          Ask for explanations, study strategies, or vocabulary help. Every
          response below is a draft — the assistant is not yet live in this
          MVP.
        </p>
      </header>

      <PrivacyBanner />

      {state.status === 'loading' ? (
        <LoadingState label="Loading transcript…" />
      ) : state.status === 'error' ? (
        <ErrorState error={state.error} />
      ) : state.data.length === 0 ? (
        <EmptyState
          title="No transcript yet"
          description="When the Study Assistant ships in a later sprint, your conversation history will appear here."
        />
      ) : (
        <ol
          className="transcript"
          aria-label="Study Assistant draft transcript"
          data-testid="assistant-transcript"
        >
          {state.data.map((msg) => (
            <li
              key={msg.id}
              className={
                msg.role === 'student'
                  ? 'transcript__row transcript__row--student'
                  : 'transcript__row transcript__row--assistant'
              }
              data-testid={`assistant-message-${msg.role}`}
            >
              <div className="transcript__meta">
                <span className="transcript__role">
                  {msg.role === 'student' ? 'You' : 'Assistant'}
                </span>
                {msg.role === 'assistant' ? (
                  <span
                    className="badge transcript__draft-badge"
                    data-testid="assistant-draft-badge"
                  >
                    Draft
                  </span>
                ) : null}
                <time className="muted" dateTime={msg.createdAt}>
                  {formatDate(msg.createdAt)}
                </time>
              </div>
              <p className="transcript__body">{msg.content}</p>
            </li>
          ))}
        </ol>
      )}

      <form
        className="compose"
        aria-label="Compose a message"
        onSubmit={(e) => e.preventDefault()}
      >
        <label className="field">
          <span>Message (assistant is offline this sprint)</span>
          <textarea
            className="compose__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="The Study Assistant ships later — your message stays local for now."
            disabled
            data-testid="assistant-compose"
            rows={3}
          />
        </label>
        <button
          type="submit"
          className="btn btn--primary"
          disabled
          data-testid="assistant-send"
        >
          Send (disabled)
        </button>
      </form>
    </section>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
  } catch {
    return iso;
  }
}
